import { useState } from "react";
import { uuidv7 } from "@huayruro/shared";
import { dbLocal } from "../lib/db-local";
import { encolar } from "../lib/cola";
import { flushAhora } from "../lib/useSyncPos";
import { separarCodigoONombre } from "../lib/abrir-catalogo";

type Props = {
  /** Lo que ya se escribió en el buscador o el código que el escáner no encontró. */
  inicial: string;
  onListo: (mensaje: string) => void;
  onCancelar: () => void;
};

// «Sí lo tengo» (RU-1, Kevin 27-sep-2026): el producto está en la mano y el sistema no lo encuentra.
// NO es un faltante (el producto sí hay): se anota para que el Encargado cree la ficha en Catálogo.
// Esa venta no entra al sistema hoy, y la ventana lo dice. Funciona sin conexión por la misma cola
// idempotente del faltante.
export function PorCrearModal({ inicial, onListo, onCancelar }: Props) {
  const separado = separarCodigoONombre(inicial);
  const [gtin, setGtin] = useState(separado.gtin ?? "");
  const [nombre, setNombre] = useState(separado.nombre ?? "");
  const [enviando, setEnviando] = useState(false);
  const vacio = !gtin.trim() && !nombre.trim();

  async function anotar() {
    if (vacio) return;
    setEnviando(true);
    try {
      await encolar(dbLocal, "por_crear", {
        client_uuid: uuidv7(),
        gtin: gtin.trim() || null,
        descripcion: nombre.trim() || null,
      });
      flushAhora();
      onListo("Anotado para el Encargado");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-surface/40 backdrop-blur p-4">
      <div role="dialog" aria-labelledby="por-crear-titulo" className="w-full max-w-md bg-card border border-line rounded-[14px] p-6 shadow-[0_10px_30px_rgba(36,29,26,0.25)]">
        <h2 id="por-crear-titulo" className="text-xl font-bold text-ink">
          Sí lo tengo
        </h2>
        <p className="text-[13px] text-ink-2 mt-1">
          Lo tienes en la mano y el sistema no lo encuentra. El Encargado lo verá en Catálogo para crear la ficha. Esta venta no
          queda registrada hoy.
        </p>

        <div className="mt-4">
          <label htmlFor="por-crear-codigo" className="block text-[13px] mb-1 text-ink-2">
            Código de barras
          </label>
          <input
            id="por-crear-codigo"
            type="text"
            inputMode="numeric"
            value={gtin}
            onChange={(e) => setGtin(e.target.value)}
            className="w-full min-h-[44px] px-3 py-2 rounded-[9px] bg-field border border-line-input focus:border-ok outline-none text-ink font-mono"
            placeholder="Opcional"
          />
        </div>
        <div className="mt-3">
          <label htmlFor="por-crear-nombre" className="block text-[13px] mb-1 text-ink-2">
            Nombre (como dice la caja)
          </label>
          <input
            id="por-crear-nombre"
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className="w-full min-h-[44px] px-3 py-2 rounded-[9px] bg-field border border-line-input focus:border-ok outline-none text-ink"
            placeholder="Ej. Panadol Antigripal x12"
          />
        </div>

        <div className="mt-6 flex gap-3">
          <button
            onClick={onCancelar}
            disabled={enviando}
            className="flex-1 min-h-[44px] py-2.5 rounded-[9px] bg-card border border-line-input text-ink-emph hover:bg-hover-btn text-sm disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={() => void anotar()}
            disabled={enviando || vacio}
            className="flex-1 min-h-[44px] py-2.5 rounded-[9px] bg-ok hover:opacity-90 text-white font-semibold disabled:opacity-30"
          >
            {enviando ? "Anotando..." : "Anotar"}
          </button>
        </div>
      </div>
    </div>
  );
}
